import React, { useContext, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";

import logo from "../assets/shoe.png";

// Panel Pages
import DashboardOverview from "./DashboardOverview";
import Products from "./Products";
import Inventory from "./Inventory";
import Sales from "./Sales";
import Customers from "./Customers";
import Debts from "./Debts";
import Receipts from "./Receipts";

// Sales Report Sub-pages
import StockTrackingReport from "./StockTrackingReport";
import DebtsTrackingReport from "./DebtsTrackingReport";
import DailySalesSummary from "./DailySalesSummary";
import TopSellingProducts from "./TopSellingProducts";
import SalesByStaff from "./SalesByStaff";

/* ----------------------------------------
   API helpers (avoid 404 from port :3000)
   ---------------------------------------- */
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) ||
  (typeof process !== "undefined" && process.env?.REACT_APP_API_BASE) ||
  (typeof window !== "undefined"
    ? window.location.origin.replace(":3000", "")
    : "");

// Pull token either from AuthContext.user or localStorage
const authHeader = (user) => {
  const token =
    (user && (user.token || user.accessToken)) ||
    localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/* ----------------------------------------
   Avatar utilities (no DB column required)
   ---------------------------------------- */
const localAvatarKey = (userId) => `profilePhoto_user_${userId}`;

function useLocalAvatar(user) {
  const [dataUrl, setDataUrl] = useState(() => {
    if (!user?.id) return null;
    return localStorage.getItem(localAvatarKey(user.id));
  });

  const save = async (file) => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onload = () => {
        try {
          if (!user?.id) return reject(new Error("Missing user id"));
          localStorage.setItem(localAvatarKey(user.id), reader.result);
          setDataUrl(reader.result);
          resolve(reader.result);
        } catch (e) {
          reject(e);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const remove = () => {
    if (!user?.id) return;
    localStorage.removeItem(localAvatarKey(user.id));
    setDataUrl(null);
  };

  return { dataUrl, save, remove };
}

const AvatarCircle = ({ name = "", size = 40, src }) => {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  if (src) {
    return (
      <img
        src={src}
        alt={name || "avatar"}
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <div
      className="rounded-full flex items-center justify-center font-semibold text-white"
      style={{
        width: size,
        height: size,
        background:
          "linear-gradient(135deg, rgba(37,99,235,1) 0%, rgba(59,130,246,1) 100%)",
      }}
      aria-label={name || "avatar"}
    >
      {initials || "U"}
    </div>
  );
};

/* ----------------------------------------
   Profile Modal
   ---------------------------------------- */
const ProfileModal = ({ isOpen, onClose, user, onLogout }) => {
  const { dataUrl, save, remove } = useLocalAvatar(user);
  const [preview, setPreview] = useState(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setPreview(null);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMsg(null);
      setErr(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreview({ file, url });
  };

  const handleSaveAvatar = async () => {
    if (!preview?.file) return;
    try {
      setBusy(true);
      await save(preview.file);
      setMsg("Profile photo updated.");
      setPreview(null);
    } catch {
      setErr("Failed to save profile photo.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      setBusy(true);
      remove();
      setMsg("Profile photo removed.");
      setPreview(null);
    } catch {
      setErr("Failed to remove profile photo.");
    } finally {
      setBusy(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMsg(null);
    setErr(null);

    if (!currentPassword || !newPassword) {
      setErr("Please fill all password fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErr("New password and confirmation do not match.");
      return;
    }

    try {
      setBusy(true);
      const res = await fetch(
        `${API_BASE}/shop/routes/users.php?action=change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeader(user),
          },
          // credentials: "include", // if your PHP uses cookies
          body: JSON.stringify({ currentPassword, newPassword }),
        }
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.ok === false) {
        throw new Error(j?.error || j?.message || "Failed to change password.");
      }
      setMsg("Password changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-3"
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-title"
    >
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 id="profile-title" className="text-lg font-semibold">
            Profile & Account
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-gray-100"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: avatar + logout */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-4">
              <AvatarCircle
                name={user?.username}
                size={72}
                src={preview?.url || dataUrl}
              />
              <div>
                <p className="font-semibold">{user?.username}</p>
                <p className="text-sm text-gray-500">
                  Role: {user?.role} • Shop: {user?.shop_id ?? "—"}
                </p>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <label className="inline-block">
                <span className="sr-only">Choose profile photo</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePick}
                  className="hidden"
                  id="pick-avatar"
                />
                <button
                  onClick={() =>
                    document.getElementById("pick-avatar")?.click()
                  }
                  className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm"
                >
                  Choose Photo
                </button>
              </label>

              {preview && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSaveAvatar}
                    disabled={busy}
                    className="px-3 py-2 rounded bg-green-600 text-white hover:bg-green-700 text-sm disabled:opacity-60"
                  >
                    Save Photo
                  </button>
                  <button
                    onClick={() => setPreview(null)}
                    disabled={busy}
                    className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-60"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {(dataUrl || preview) && !preview && (
                <button
                  onClick={handleRemoveAvatar}
                  disabled={busy}
                  className="px-3 py-2 rounded bg-red-50 text-red-600 hover:bg-red-100 text-sm disabled:opacity-60"
                >
                  Remove Photo
                </button>
              )}
            </div>

            <div className="mt-6">
              <button
                onClick={onLogout}
                className="w-full bg-red-600 hover:bg-red-700 py-2 rounded text-white font-semibold"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Right: change password form */}
          <div className="lg:col-span-2">
            <h4 className="font-semibold mb-3">Change Password</h4>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  className="w-full border rounded px-3 py-2"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    className="w-full border rounded px-3 py-2"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    className="w-full border rounded px-3 py-2"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
              </div>

              {msg && <p className="text-green-600 text-sm">{msg}</p>}
              {err && <p className="text-red-600 text-sm">{err}</p>}

              <div className="pt-1">
                <button
                  type="submit"
                  disabled={busy}
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  Save Changes
                </button>
              </div>
            </form>

            <p className="text-xs text-gray-500 mt-4">
              You can later switch photo storage to{" "}
              <code>/shop/routes/users.php?action=avatar-upload</code> without
              altering the users table—save files as <code>user_{"{id}"}.jpg</code> and
              return a URL.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ----------------------------------------
   Main Dashboard (keeps existing logic)
   ---------------------------------------- */
const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [activePanel, setActivePanel] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSalesReports, setShowSalesReports] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const { dataUrl } = useLocalAvatar(user);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const allNavItems = useMemo(
    () => [
      { label: "Dashboard", key: "dashboard" },
      { label: "General Store", key: "products" },
      { label: "Branch Store", key: "inventory" },
      { label: "Start Selling", key: "sales" },
      { label: "Receipts", key: "receipts" },
      { label: "Debts", key: "debts" },
      { label: "Customers", key: "customers" },
      {
        label: "Sales Report",
        key: "sales-report",
        submenu: [
          { label: "Stock Tracking", key: "sales-report-stock" },
          { label: "Debts Tracking", key: "sales-report-debts" },
          { label: "Daily Sales Summary", key: "sales-report-daily" },
          { label: "Top Selling Products", key: "sales-report-top" },
          { label: "Sales by Staff", key: "sales-report-staff" },
        ],
      },
    ],
    []
  );

  const HIDE_SIDEBAR_KEYS = new Set([
    "debts",
    "sales-report-daily",
    "sales-report-top",
    "sales-report-staff",
  ]);

  const roleAllowedKeysForStaff = new Set([
    "dashboard",
    "sales",
    "receipts",
    "sales-report",
  ]);

  const baseNav =
    user?.role === "staff"
      ? allNavItems.filter(
          (item) => roleAllowedKeysForStaff.has(item.key) || item.submenu
        )
      : allNavItems;

  const navItems = baseNav
    .map((item) => {
      if (!item.submenu) return item;
      const filteredSub = item.submenu.filter(
        (sub) => !HIDE_SIDEBAR_KEYS.has(sub.key)
      );
      return { ...item, submenu: filteredSub };
    })
    .filter((item) => !HIDE_SIDEBAR_KEYS.has(item.key));

  const renderPanel = () => {
    switch (activePanel) {
      case "dashboard":
        return <DashboardOverview />;
      case "products":
        return <Products />;
      case "inventory":
        return <Inventory />;
      case "sales":
        return <Sales />;
      case "receipts":
        return <Receipts />;
      case "debts":
        return <Debts />;
      case "customers":
        return <Customers />;
      case "sales-report-stock":
        return <StockTrackingReport />;
      case "sales-report-debts":
        return <DebtsTrackingReport />;
      case "sales-report-daily":
        return <DailySalesSummary />;
      case "sales-report-top":
        return <TopSellingProducts />;
      case "sales-report-staff":
        return <SalesByStaff />;
      default:
        return <div>Not Found</div>;
    }
  };

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 w-full bg-blue-900 p-4 z-50 flex justify-between items-center">
        <h2 className="text-white text-lg font-bold">Petrah Admin</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setProfileOpen(true)}
            className="rounded-full ring-2 ring-white/40"
            aria-label="Open profile"
          >
            <AvatarCircle name={user?.username} size={32} src={dataUrl} />
          </button>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white focus:outline-none"
            aria-label="Toggle menu"
          >
            ☰
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed z-40 top-0 left-0 h-full bg-blue-900 text-white shadow-md flex flex-col transition-transform duration-300 ease-in-out transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:relative lg:flex lg:w-64`}
      >
        <div className="p-6 flex flex-col items-center border-b border-blue-700 mt-12 lg:mt-0">
          <img src={logo} alt="Logo" className="h-20 w-20 rounded-full mb-2" />
          <h2 className="text-lg font-bold">Petrah Admin</h2>
          <p className="text-sm text-blue-200">Welcome, {user?.username}</p>

          <button
            onClick={() => setProfileOpen(true)}
            className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded bg-white/10 hover:bg-white/20"
          >
            <AvatarCircle name={user?.username} size={20} src={dataUrl} />
            <span className="text-sm">Profile</span>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4">
          {navItems.map((item) =>
            item.submenu ? (
              <div key={item.key} className="mb-2">
                <button
                  onClick={() => setShowSalesReports((prev) => !prev)}
                  className={`w-full text-left px-4 py-2 rounded hover:bg-blue-700 transition ${
                    showSalesReports ? "bg-blue-700" : ""
                  }`}
                >
                  {item.label}
                </button>
                {showSalesReports && item.submenu.length > 0 && (
                  <div className="ml-4 mt-1">
                    {item.submenu.map((sub) => (
                      <button
                        key={sub.key}
                        onClick={() => {
                          setActivePanel(sub.key);
                          setSidebarOpen(false);
                        }}
                        className={`block w-full text-left px-4 py-2 mb-1 rounded hover:bg-blue-600 transition ${
                          activePanel === sub.key ? "bg-blue-600" : ""
                        }`}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <button
                key={item.key}
                onClick={() => {
                  setActivePanel(item.key);
                  setSidebarOpen(false);
                }}
                className={`block w-full text-left px-4 py-2 mb-2 rounded hover:bg-blue-700 transition ${
                  activePanel === item.key ? "bg-blue-700" : ""
                }`}
              >
                {item.label}
              </button>
            )
          )}
        </nav>

        <div className="p-4 border-t border-blue-800">
          {/* ✅ FIX: use handleLogout here */}
          <button
            onClick={handleLogout}
            className="w-full bg-red-600 hover:bg-red-700 py-2 rounded text-white font-bold transition"
          >
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 mt-16 lg:mt-0 p-4 lg:p-8 bg-gray-50 overflow-y-auto w-full">
        <div className="bg-white p-4 lg:p-6 rounded shadow-lg transition-all duration-300 ease-in-out">
          {renderPanel()}
        </div>
      </main>

      {/* Profile modal */}
      <ProfileModal
        isOpen={profileOpen}
        onClose={() => setProfileOpen(false)}
        user={user}
        onLogout={handleLogout}
      />
    </div>
  );
};

export default Dashboard;
